import {
	ApolloClient,
	InMemoryCache,
	HttpLink,
	ApolloLink,
	CombinedGraphQLErrors,
	CombinedProtocolErrors,
	ServerError,
} from '@apollo/client';
import { SetContextLink } from '@apollo/client/link/context';
import { ErrorLink } from '@apollo/client/link/error';
import { PersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { sha256 } from 'crypto-hash';
import { useMemo } from 'react';
import { map } from 'rxjs';

import type { UpdateTimeOffsetDetail } from '#cutils/context/TimeContext.utils';
import type { AppPageProps } from '#cutils/page-context/server-side-props';
import { logger } from '#utils/logging/logger';

import fragmentMatcherData from './fragmentMatcherData';
import { typePolicies } from './type-policies';

const APOLLO_STATE_PROP_NAME = '__APOLLO_STATE__';

let apolloClient: ApolloClient;

const defaultOptionsClient: ApolloClient.DefaultOptions.Input = {
	query: { fetchPolicy: 'cache-first', errorPolicy: 'all' },
	watchQuery: { fetchPolicy: 'cache-first', errorPolicy: 'all' },
};

const location = typeof window === 'undefined' ? 'server' : 'client';

interface ClientOptions {
	uri: string;
	branch: string;
	defaultOptions: ApolloClient.DefaultOptions.Input;
	accessToken?: string;
	enableServerDate: boolean;
}

const setServerDate = new ApolloLink((operation, forward) => {
	return forward(operation).pipe(
		map((response) => {
			const context = operation.getContext();
			const headers: Headers = context.response.headers;

			if (headers && headers.has('date')) {
				const dateHeader = headers.get('date')!;

				const serverDate = Date.parse(dateHeader);

				if (!Number.isNaN(serverDate)) {
					const timeOffset = serverDate - Date.now();

					window.dispatchEvent(new CustomEvent<UpdateTimeOffsetDetail>('br24.updateCurrentTime', { detail: { timeOffset } }));
				}
			}

			return response;
		})
	);
});

function createLink(options: ClientOptions) {
	const httpLinkOptions: HttpLink.Options = { uri: `${options.uri}/graphql` };

	if (options?.accessToken) {
		httpLinkOptions.headers = { authorization: `Bearer ${options.accessToken}` };
	}

	return ApolloLink.from([
		new ErrorLink(({ error, operation }) => {
			try {
				if (CombinedGraphQLErrors.is(error)) {
					const errors = error.errors.map(({ message, locations, path, extensions }) => ({ message, locations, path, extensions }));

					logger.error({ errors, graphqlOperationName: operation.operationName }, 'Graphql semantic error');
				} else if (CombinedProtocolErrors.is(error)) {
					const errors = error.errors.map(({ message, locations, path, extensions }) => ({ message, locations, path, extensions }));

					logger.error({ errors, graphqlOperationName: operation.operationName }, 'Graphql protocol error');
				} else if (ServerError.is(error)) {
					const message = error.message;
					const name = error.name;
					const statusCode = error.statusCode;

					logger.error(
						{
							networkError: { name, message, statusCode },
							response: error.bodyText,
							graphqlOperationName: operation.operationName,
						},
						`Graphql network error`
					);
				} else {
					logger.error(error, `Graphql error`);
				}
			} catch (error) {
				logger.error(error, `Error during graphql error reporting`);
			}
		}),

		new SetContextLink(() => {
			const uaHeader = `br24-web (${location}-side;${options.branch})`;

			const contextOptions: { headers: Record<string, string | Headers> } = {
				headers: {
					'x-client': uaHeader,
					'User-Agent': uaHeader,
				},
			};

			if (options.accessToken) {
				contextOptions.headers['authorization'] = `Bearer ${options.accessToken}`;
			}

			return contextOptions;
		}),
		...(options.enableServerDate ? [setServerDate] : []),
		new PersistedQueryLink({
			sha256,
			// DO NOT REMOVE THIS LINE! DO NOT REMOVE THIS LINE! DO NOT REMOVE THIS LINE! DO NOT REMOVE THIS LINE!
			//
			// Akamai documentation:
			//   Allow use of the POST HTTP request method. By default, GET, HEAD and OPTIONS are the only method
			//   honored, and others are denied with a 403. With this behavior enabled, POST requests pass to the
			//   origin, and do not cache.
			useGETForHashedQueries: true,
		}),
		new HttpLink(httpLinkOptions),
	]);
}

// After debugging for a couple of hours I learned:
// It is important that we create the `ApolloClient` & `InMemoryCache` every time we make a new request.
// This is why we do it inside the `withApollo` export. If we don't, the data gets stale on SSR because the cache is never
// updated as long as the server runs.

function createApolloClient(options: ClientOptions) {
	const apolloOptions: ApolloClient.Options = {
		ssrMode: typeof window === 'undefined',
		cache: new InMemoryCache({
			possibleTypes: fragmentMatcherData.possibleTypes,
			typePolicies,
		}),
		link: createLink(options),
		defaultOptions: options.defaultOptions,
		devtools: {
			enabled: options.branch !== 'master',
		},
	};

	return new ApolloClient(apolloOptions);
}

export function initializeApollo(initialState = null, options: ClientOptions) {
	const _apolloClient = apolloClient ?? createApolloClient(options);

	// If your page has Next.js data fetching methods that use Apollo Client, the initial state
	// gets hydrated here

	// reset the store if a page navigation occurs (merging caches between pages is very hard to achieve)
	if (initialState) {
		_apolloClient.restore(initialState);
	}

	// For SSG and SSR always create a new Apollo Client
	if (typeof window === 'undefined') {
		return _apolloClient;
	}

	// Create the Apollo Client once in the client
	if (!apolloClient) {
		apolloClient = _apolloClient;
	}

	return apolloClient;
}

export function addApolloState(client: ApolloClient, pageProps: any) {
	if (pageProps?.props) {
		pageProps.props[APOLLO_STATE_PROP_NAME] = client.cache.extract();
	}

	return pageProps;
}

export function useApollo(pageProps: AppPageProps) {
	// @ts-expect-error the apollo state is hidden from the type as it should only be used here and when serializing the state
	const state = pageProps[APOLLO_STATE_PROP_NAME];
	const enableServerDate = pageProps.environment.flags.ENABLE_SERVER_DATE;
	const store = useMemo(
		() =>
			initializeApollo(state, {
				uri: pageProps.environment.endpoints.ENDPOINT_GRAPHQL,
				branch: pageProps.environment.constants.BRANCH,
				defaultOptions: defaultOptionsClient,
				enableServerDate,
			}),
		[state, pageProps.environment.endpoints.ENDPOINT_GRAPHQL, pageProps.environment.constants.BRANCH, enableServerDate]
	);

	return store;
}
